// TARJETA DE FILTROS PLEGABLE (catálogo; operador, 5-oct-2026: «los filtros deberían ser
// colapsables y aparecer inicialmente abiertos»).
//
// La misma superficie que `Card`, con la cabecera como DISCLOSURE (WAI-ARIA): el título
// «Filtros» es un botón con `aria-expanded` y `aria-controls` y un chevrón que gira. Nace
// abierta en cada visita. Plegada, la cabecera dice cuántos filtros hay puestos («2 activos»),
// para que nadie lea una lista filtrada creyendo que es la entera. Las acciones de la cabecera
// («Quitar filtros») se ven siempre, plegada o no.
//
// El cuerpo se oculta con `hidden` y no se desmonta: los controles conservan lo escrito y la
// lista sigue filtrada mientras está plegada.
import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, SlidersHorizontal } from 'lucide-react';
import { StatusBadge, SUPERFICIE } from '../ui';

export type PropsDeFiltrosColapsables = {
  children: ReactNode;
  /** Acciones a la derecha de la cabecera, visibles siempre (p. ej. «Quitar filtros»). */
  actions?: ReactNode;
  /** Cuántos filtros están puestos: se anuncia en la cabecera cuando está plegada. */
  activos?: number;
  /** El título de la tarjeta. */
  titulo?: string;
  /** Solo para pruebas o casos especiales: por defecto nace abierta. */
  abiertaAlInicio?: boolean;
  className?: string;
  /** Base de los testid: el propio (la tarjeta), `-alternar` (el botón) y `-cuerpo`. */
  'data-testid'?: string;
};

export default function FiltrosColapsables({
  children, actions, activos = 0, titulo = 'Filtros', abiertaAlInicio = true, className = '', 'data-testid': testid,
}: PropsDeFiltrosColapsables) {
  const [abierta, setAbierta] = useState(abiertaAlInicio);
  const idDelCuerpo = useId();
  const t = (sufijo: string) => (testid ? `${testid}-${sufijo}` : undefined);
  return (
    <section data-testid={testid} aria-label={titulo} className={SUPERFICIE + ' ' + className}>
      <div className={'flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-5 py-3 '
        + (abierta ? 'border-b border-hairline' : '')}>
        <button type="button" onClick={() => setAbierta((v) => !v)} aria-expanded={abierta} aria-controls={idDelCuerpo}
          data-testid={t('alternar')}
          className="-ml-2 flex min-h-10 items-center gap-2.5 rounded-lg px-2 text-left cursor-pointer hover:bg-canvas transition-colors">
          <SlidersHorizontal size={18} className="shrink-0 text-marca-texto" aria-hidden="true" />
          <h2 className="text-titulo font-semibold text-ink">{titulo}</h2>
          {!abierta && activos > 0 && (
            <StatusBadge tone="info" dot={false} data-testid={t('activos')}>
              {activos === 1 ? '1 activo' : `${activos} activos`}
            </StatusBadge>
          )}
          <ChevronDown size={16} aria-hidden="true"
            className={'shrink-0 text-ink-soft transition-transform ' + (abierta ? 'rotate-180' : '')} />
        </button>
        {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
      </div>
      <div id={idDelCuerpo} hidden={!abierta} data-testid={t('cuerpo')} className="p-5">
        {children}
      </div>
    </section>
  );
}
